import { Box, Divider, Flex, Image, Stack, Text, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';
import InputNames from '../../custom-components/text/InputNames';
import Input from '../../custom-components/Inputs/Input';
import ZaraSelect from '../../custom-components/select/ZaraSelect';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ChangeProfile } from '../../../graphql/mutations/ChangeProfile';
import { useCloseEvent, useGetProfileMenu } from '../../../hooks';
import ZaraButton from '../../custom-components/buttons/Button';
import { GetMe } from '../../../graphql/queries/GetMe';
import { useError } from '../../../error-provider/ErrorProvider';
import { z } from 'zod';
import { companyNameSchema, languageSchema, nameSchema, phoneSchema, websiteSchema } from '../../../utils/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormInput from '../../custom-components/Inputs/FormInput';
import { INPUT_PROPS } from '../../../utils/constants';
import 'react-phone-input-2/lib/style.css';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useAuth } from '../../../provider';
import { PATHS } from '../../../router/routes';
import leftIcon from '../../../assets/icons/left-icon.svg';
import trainingIcon from '../../../assets/icons/education.svg';
import TrustpilotWidget from '../../custom-components/menu/TrustPilotWidget';

export const digitPattern = /^[\d+]*$/;

const schema = z
    .object({
        firstName: nameSchema,
        lastName: nameSchema,
        companyName: companyNameSchema,
        phoneNumber: phoneSchema,
        website: websiteSchema,
        language: languageSchema,
    })
    .refine((data) => digitPattern.test(data.phoneNumber), {
        message: 'Invalid number',
        path: ['phoneNumber'],
    });

const ProfileInfoComponent = () => {
    const { showError } = useError();
    const toast = useToast();
    const router = useNavigate();
    const location = useLocation();
    const { pathname } = location;

    const { signOut } = useAuth();

    const logoutHandler = () => {
        signOut();
        router(PATHS.SIGN_IN);
    };

    const apolloClient = useApolloClient();

    const { data: userMe } = useQuery(GetMe, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const userEmail = userMe?.getMe?.email;
    const language = userMe?.getMe?.language;
    const phoneNumber = userMe?.getMe?.phoneNumber;
    const companyName = userMe?.getMe?.company.name;
    const website = userMe?.getMe?.website;
    const firstName = userMe?.getMe?.firstName;
    const lastName = userMe?.getMe?.lastName;

    const { control, handleSubmit } = useForm({
        mode: 'onSubmit',
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: firstName ?? '',
            lastName: lastName ?? '',
            language: language,
            phoneNumber: phoneNumber ?? '',
            companyName: companyName ?? '',
            website: website ?? '',
        },
    });

    const profileMenu = useGetProfileMenu();

    const mobileProfileMenu = useGetProfileMenu(true);

    const checkCurrentPage = (page) => {
        if (pathname === page) {
            return true;
        } else {
            return false;
        }
    };

    const languages = [{ name: 'English', value: 1 }];

    const [isLangSelectFocus, setIsLangSelectFocus] = useState(false);

    useCloseEvent(setIsLangSelectFocus);

    const [onChangeProfile, { loading }] = useMutation(ChangeProfile, {
        onError(error) {
            showError(undefined, error);
        },
    });

    async function onSubmit(data) {
        const variables = {
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            companyName: data.companyName,
            website: data.website,
            language: Number(data.language),
        };

        await onChangeProfile({
            variables: variables,
            async onCompleted(data) {
                if (data.changeProfile.__typename === 'ChangeProfileResponse') {
                    toast({
                        title: data.changeProfile.message,
                        status: 'success',
                        isClosable: true,
                    });

                    await apolloClient.writeQuery({
                        query: GetMe,
                        data: {
                            getMe: {
                                ...userMe.getMe,
                                firstName: variables.firstName,
                                lastName: variables.lastName,
                                website: variables.website,
                                phoneNumber: variables.phoneNumber,
                                language: variables.language,
                                company: {
                                    ...userMe.getMe.company,
                                    name: variables.companyName,
                                },
                            },
                        },
                    });
                }
            },
        });
    }

    return (
        <>
            <Flex
                minH='560px'
                boxShadow=' 0px 1px 3px 0px #0000001A'
                mt='6'
                w='100%'
                css={{
                    '@media only screen and (max-width: 920px)': {
                        marginLeft: '0px',
                    },

                    '@media only screen and (min-width: 500px)': {
                        display: 'flex',
                    },
                    '@media only screen and (max-width: 450px)': {
                        display: 'none',
                    },
                    '@media only screen and (max-height: 450px)': {
                        marginBottom: '100px',
                    },
                }}
                border='1px solid #E2E8F0'
                borderRadius='4px'
            >
                <Stack p='10px' spacing='10px' w='278px' borderRight='1px solid #E2E8F0'>
                    {profileMenu.map((menuItem) => (
                        <Box
                            onClick={() => {
                                router(menuItem.path);
                            }}
                            cursor='pointer'
                            css={{
                                ':hover': {
                                    background: checkCurrentPage(menuItem.path) ? '#F7FAFC' : 'white',
                                    border: checkCurrentPage(menuItem.path) ? '1px solid white' : '1px solid #E2E8F0',
                                },
                                ':active': {
                                    background: checkCurrentPage(menuItem.path) ? '#F7FAFC' : 'white',
                                },
                            }}
                            bg={checkCurrentPage(menuItem.path) ? '#F7FAFC' : 'white'}
                            textAlign='left'
                            p='6px 12px'
                            border='1px solid white'
                            key={menuItem.name}
                        >
                            <Text fontSize='16px' lineHeight='28px' fontWeight='400'>
                                {menuItem.name}
                            </Text>
                        </Box>
                    ))}
                </Stack>

                <Box h='100%' w='100%'>
                    <Box as='form' onSubmit={handleSubmit(onSubmit)}>
                        <Stack spacing='3' p='20px'>
                            <Flex justifyContent='space-between'>
                                <FormInput
                                    w='45%'
                                    control={control}
                                    label={INPUT_PROPS.FIRST_NAME.LABEL}
                                    controllName={INPUT_PROPS.FIRST_NAME.CONTROLL_NAME}
                                    placeholder={INPUT_PROPS.FIRST_NAME.PLACEHOLDER}
                                />
                                <FormInput
                                    w='45%'
                                    control={control}
                                    label={INPUT_PROPS.LAST_NAME.LABEL}
                                    controllName={INPUT_PROPS.LAST_NAME.CONTROLL_NAME}
                                    placeholder={INPUT_PROPS.LAST_NAME.PLACEHOLDER}
                                />
                            </Flex>

                            <Box mb='10px'>
                                <InputNames color='#2D3748'>Email</InputNames>
                                <Input value={userEmail} isDisabled={true} />
                            </Box>

                            <FormInput
                                control={control}
                                label={INPUT_PROPS.PHONE_NUMBER.LABEL}
                                controllName={INPUT_PROPS.PHONE_NUMBER.CONTROLL_NAME}
                                placeholder={INPUT_PROPS.PHONE_NUMBER.PLACEHOLDER}
                            />

                            <Box
                                css={{
                                    '@media only screen and (max-width: 950px)': {
                                        display: 'none',
                                    },
                                }}
                                mb='10px'
                            >
                                <Flex justifyContent='space-between'>
                                    <Box w='240px' textAlign='left'>
                                        <InputNames color='#2D3748'>Company</InputNames>
                                    </Box>

                                    <Box w='240px' textAlign='left'>
                                        <InputNames>Website</InputNames>
                                    </Box>
                                </Flex>

                                <Flex
                                    justifyContent='space-between'
                                    css={{
                                        '@media only screen and (max-width: 950px)': {
                                            display: 'none',
                                        },
                                    }}
                                >
                                    <FormInput
                                        mb='0px'
                                        control={control}
                                        controllName={INPUT_PROPS.COMPANY.CONTROLL_NAME}
                                        placeholder={INPUT_PROPS.COMPANY.PLACEHOLDER}
                                    />

                                    <FormInput
                                        mb='0px'
                                        control={control}
                                        controllName={INPUT_PROPS.WEBSITE.CONTROLL_NAME}
                                        placeholder={INPUT_PROPS.WEBSITE.PLACEHOLDER}
                                    />
                                </Flex>
                            </Box>
                            <FormInput
                                css={{
                                    '@media only screen and (min-width: 950px)': {
                                        display: 'none',
                                    },
                                    '@media only screen and (max-width: 950px)': {
                                        display: 'block',
                                    },
                                }}
                                control={control}
                                label='Company'
                                controllName={INPUT_PROPS.COMPANY.CONTROLL_NAME}
                                placeholder={INPUT_PROPS.COMPANY.PLACEHOLDER}
                            />
                            <FormInput
                                css={{
                                    '@media only screen and (min-width: 950px)': {
                                        display: 'none',
                                    },
                                    '@media only screen and (max-width: 950px)': {
                                        display: 'block',
                                    },
                                }}
                                control={control}
                                label='Website'
                                controllName={INPUT_PROPS.WEBSITE.CONTROLL_NAME}
                                placeholder={INPUT_PROPS.WEBSITE.PLACEHOLDER}
                            />
                            <Box>
                                <InputNames color='#2D3748'>Language</InputNames>
                                <Controller
                                    control={control}
                                    name='language'
                                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                                        <ZaraSelect
                                            isSelectFocus={isLangSelectFocus}
                                            setIsSelectFocus={setIsLangSelectFocus}
                                            selectValue={value}
                                            onBlur={onBlur}
                                            setSelectValue={onChange}
                                            placeholder='Choose language'
                                        >
                                            {languages.map((language) => (
                                                <option
                                                    key={language.value}
                                                    disabled={language.value === value}
                                                    value={language.value}
                                                >
                                                    {language.name}
                                                </option>
                                            ))}
                                        </ZaraSelect>
                                    )}
                                />
                            </Box>
                            <Box textAlign='right'>
                                <ZaraButton isLoading={loading} isDisabled={loading} type='submit'>
                                    Save
                                </ZaraButton>
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </Flex>

            {/*Mobile version */}
            <Box
                css={{
                    '@media only screen and (min-width: 450px)': {
                        display: 'none',
                    },
                    '@media only screen and (max-width: 450px)': {
                        display: 'block',
                    },
                }}
                w='100%'
                mt='70px'
                mb='100px'
            >
                <Box mb='10px'>
                    {mobileProfileMenu.map((menuItem) => (
                        <Flex
                            justifyContent='space-between'
                            alignItems='center'
                            textAlign='left'
                            onClick={() => router(menuItem.path)}
                            p='16px 12px'
                            key={menuItem.name}
                        >
                            <Text fontSize='16px' lineHeight='28px' fontWeight='400'>
                                {menuItem.name}
                            </Text>
                            <ChevronRightIcon w='25px' h='25px' />
                        </Flex>
                    ))}
                </Box>
                <Divider mt='-10px' bg='#D9D9D9' h='2px' />

                <Flex cursor='pointer' pos='absolute' bottom='22px' right='0px'>
                    <TrustpilotWidget />
                </Flex>

                <Flex
                    w='340px'
                    p='16px 12px'
                    alignItems='center'
                    onClick={() => window.open(' https://guide.dialzara.com', '_blank')}
                    pos='absolute'
                    bottom='80px'
                    h='40px'
                >
                    <Image mr='8px' src={trainingIcon} />
                    <Text textAlign='left' fontSize='16px' fontWeight='600' lineHeight='24px'>
                        Help
                    </Text>
                </Flex>
                <Flex
                    w='340px'
                    p='16px 12px'
                    alignItems='center'
                    onClick={logoutHandler}
                    pos='absolute'
                    bottom='20px'
                    h='40px'
                >
                    <Image mr='8px' src={leftIcon} />
                    <Text textAlign='left' fontSize='16px' fontWeight='600' lineHeight='24px'>
                        Log out
                    </Text>
                </Flex>
            </Box>
        </>
    );
};

export default ProfileInfoComponent;
